import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api from "../lib/api";
import { Reveal, Eyebrow } from "../components/Reveal";
import { EnquiryModal } from "../components/EnquiryModal";
import { useTitle } from "../hooks/useTitle";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useTitle(post ? post.title : "Article");

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="py-32 text-center" data-testid="post-not-found">
        <h1 className="font-display font-extrabold text-2xl text-[#0A192F]">Article not found</h1>
        <Link to="/blog" className="text-[#0F5132] font-bold text-sm mt-4 inline-block">← Back to Knowledge Centre</Link>
      </div>
    );
  if (!post) return <div className="py-32 text-center text-sm text-[#627D98]">Loading article...</div>;

  return (
    <div data-testid="blog-detail-page">
      <section className="bg-[#0A192F] relative overflow-hidden py-14 lg:py-20">
        <div className="absolute inset-0 hero-grid" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-6" data-testid="blog-breadcrumbs">
            <Link to="/" className="hover:text-[#C59B27]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/blog" className="hover:text-[#C59B27]">Blog</Link>
            <ChevronRight size={12} />
            <span className="text-[#C59B27]">{post.category}</span>
          </nav>
          <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl leading-[1.15]">
            {post.title}
          </h1>
          <p className="mt-5 text-slate-400 text-sm">
            By {post.author || "SterlingNest Faculty"} · {post.created_at ? new Date(post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : ""}
          </p>
        </div>
      </section>
      <article className="py-14 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {(post.content || "").split(/\n\n+/).map((para, i) => (
          <p key={i} className="text-base text-[#334E68] leading-[1.85] mb-6">
            {para}
          </p>
        ))}
        <Reveal className="mt-12">
          <div className="bg-[#0A192F] rounded-2xl p-8 relative overflow-hidden" data-testid="blog-cta">
            <div className="absolute inset-0 hero-grid" />
            <div className="relative">
              <Eyebrow light>Ready to go practical?</Eyebrow>
              <p className="font-display font-bold text-white text-lg mt-3 leading-snug">
                Reading about accounting is step one. Practising it is where careers are built.
              </p>
              <EnquiryModal
                source={`blog-${slug}`}
                trigger={
                  <button data-testid="blog-enquire-btn" className="mt-6 px-7 py-3 rounded-full bg-[#C59B27] text-[#0A192F] font-display font-bold text-sm hover:bg-[#d8ae3a] transition-colors">
                    Explore Courses
                  </button>
                }
              />
            </div>
          </div>
        </Reveal>
      </article>
    </div>
  );
}
